import { LogOut } from 'lucide-react'
import { NavLink, Outlet, useSearchParams } from 'react-router-dom'

import { useAuth } from '@/features/auth/auth-context'
import { isAdmin } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import { usePortalMe } from '@/features/portal/api'
import { DevUserSwitcher, ThemeToggle } from '@/shared/components/common'
import { EmptyState } from '@/shared/components/data'
import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'

/**
 * Shell del portal del cliente.
 *
 * Misma direccion visual, pero **sin la espina multicompany** (el cliente ve
 * una sola empresa: el color seria ruido), densidad comoda y sin el conmutador
 * `⌘K`. La diferencia se nota de un vistazo, y eso es deliberado: reduce la
 * chance de mostrar la pantalla equivocada en una llamada.
 */
/**
 * "Ver como cliente" — el **admin** mira el portal de una empresa antes de una
 * reunión, en `/portal?asCompany=<uuid>`. Es el único interno que entra: un
 * `tech_member` en `/portal` ya fue a `/403`.
 *
 * Funciona de punta a punta: el backend acepta `?as_company=` en todos los GET
 * del portal y **lo autoriza él**, sirviendo los mismos `schemas_client.py` de
 * siempre. El frontend no filtra ni simula nada de su lado; si lo hiciera
 * estaría mostrando un portal parecido al del cliente en vez del que el cliente
 * ve, que es justo lo que esta pantalla existe para evitar.
 *
 * Un interno SIN `asCompany` no tiene qué mirar: el servidor le contesta 403 en
 * cada endpoint. En vez de dibujar una pantalla de errores rojos se le dice qué
 * le falta.
 */
export function PortalLayout() {
  const t = useT()
  const { me } = useSession()
  const { signOut } = useAuth()
  const [searchParams] = useSearchParams()

  // `GET /portal/me` dice de qué empresa es quien está mirando. Si falla —a un
  // interno el portal le contesta 403— el shell igual se dibuja: el nombre de
  // la empresa es contexto, no una condición para ver la pantalla.
  const portalMe = usePortalMe()

  // Sólo el **admin** llega acá siendo interno: el router deja entrar a
  // `client` y a `admin` (`PORTAL` en `app/router.tsx`), y a un `tech_member` ya
  // lo mandó a `/403`. Preguntar por `isInternal` haría creer que hay una rama
  // para el miembro del equipo que nunca se ejecuta.
  const impersonating = isAdmin(me) ? searchParams.get('asCompany') : null

  // Un admin sin empresa elegida no tiene qué mirar: el servidor le contesta
  // 403 en cada endpoint. Se corta antes de montar el árbol, en vez de dibujar
  // siete errores que no explican qué falta.
  if (isAdmin(me) && !impersonating) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-6 text-text-1">
        <EmptyState
          title={t('nav.portal.pickCompanyTitle')}
          description={t('nav.portal.pickCompanyDescription')}
          action={
            <a className="text-xs text-signal underline" href="/companies">
              {t('nav.portal.goToCompanies')}
            </a>
          }
        />
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text-1" data-density="cozy">
      {impersonating ? (
        <div
          className="px-4 py-1.5 text-center text-2xs"
          style={{
            backgroundColor: 'hsl(var(--signal) / .16)',
            color: 'hsl(var(--text-1))',
          }}
        >
          {t('nav.portal.impersonating', {
            empresa: portalMe.data?.company_name ?? impersonating,
          })}
        </div>
      ) : null}

      <header className="hairline-b bg-surface-1">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-3">
          <span className="font-display text-base">
            {portalMe.data?.company_name ?? t('nav.portal.title')}
          </span>

          <nav className="flex items-center gap-1 text-xs" aria-label={t('nav.portal.navLabel')}>
            {[
              { to: '/portal', label: t('nav.portal.projects'), end: true },
              { to: '/portal/company', label: t('nav.portal.yourCompany'), end: false },
            ].map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'rounded-[var(--radius-control)] px-2 py-1 transition-colors duration-[var(--motion-fast)]',
                    isActive ? 'bg-surface-2 text-text-1' : 'text-text-2 hover:text-text-1',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            {/*
              El portal no tiene barra lateral, y el selector de usuario de dev
              vivia solo ahi: entrar como cliente dejaba sin forma de volver al
              lado interno. `Salir` tampoco sirve de escape en dev-auth —no hay
              Firebase que cerrar y el email sigue en `localStorage`—, asi que
              la unica salida era la consola del navegador.

              Es andamiaje, no parte de la direccion visual del portal: como el
              resto del modo dev-auth desaparece del bundle de produccion en
              tiempo de build. El portal sigue sin espina y sin `⌘K`, que es lo
              que hace que se note de un vistazo de que lado estas.
            */}
            <DevUserSwitcher />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label={t('nav.signOut')}
              title={t('nav.signOut')}
              className="rounded-[var(--radius-control)] p-1 text-text-3 hover:text-text-1"
            >
              <LogOut size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
